import { useAtom } from "jotai";
import { BasicInput } from "../../../../../components/BasicInput";
import { currentStep } from "../../../../../../app/store";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { formData } from "../useNewWithdrawModal";
import { useEffect } from "react";
import BasicOptions from "../../../../../components/BasicOptions";
import { BasicSelect } from "@/view/components/BasicSelect";
import { useQuery } from "@tanstack/react-query";
import { transactionsService } from "@/app/services/transactionService";
import { Button } from "@/view/components/ui/button";

export const bankFormSchema = z.object({
  accountType: z
    .string({ required_error: "* obrigatório" })
    .min(1, "* obrigatório"),
  bank: z.string({ required_error: "* obrigatório" }).min(1, "* obrigatório"),
  agency: z.string().min(3, "* obrigatório").max(5, "No maximo 5 caracteres"),
  account: z.string().min(4, "* obrigatório"),
});

type bankFormData = z.infer<typeof bankFormSchema>;
interface BankInfoFormProps {
  visible?: boolean;
}
export default function BankInfoForm({ visible }: BankInfoFormProps) {
  const [, setStep] = useAtom(currentStep);

  const bankForm = useForm<bankFormData>({
    resolver: zodResolver(bankFormSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = bankForm;

  const { setValue, getValues } = useFormContext<formData>();

  const submitForm = (data: bankFormData) => {
    setValue("bankAccount", data);
    setStep((state) => state + 1);
  };

  useEffect(() => {
    const values = getValues();
    if (values && values.bankAccount) reset(values.bankAccount);
  }, [reset, getValues]);

  const { data: banks, isLoading } = useQuery({
    queryKey: ["banks", {}],
    queryFn: () => transactionsService.banks(),
    select(data) {
      return data.banks.map(({ label, value }) => ({
        label: `${value} - ${label}`,
        value,
      }));
    },
  });
  return (
    <div className={visible ? "flex flex-col gap-8 max-sm:gap-4" : "hidden"}>
      <div className="grid grid-cols-inputs gap-y-4 max-sm:grid-cols-1 max-sm:gap-y-3">
        <FormProvider {...bankForm}>
          <BasicOptions
            options={[
              { label: "Poupança", value: "CP" },
              { label: "Corrente", value: "CC" },
            ]}
            name="accountType"
            label="Tipo de conta"
            error={errors.accountType?.message}
          />

          <div className="max-sm:hidden" />

          {!isLoading && (
            <div className="mr-4 max-sm:mr-0">
              <BasicSelect
                name="bank"
                label="Banco"
                error={errors.bank?.message}
                options={banks || []}
                placeholder="Selecione seu banco"
                tabIndex={6}
              />
            </div>
          )}

          <div className="mr-8 grid grid-cols-2 gap-4 max-sm:mr-0 max-sm:grid-cols-1 max-sm:gap-3">
            <BasicInput
              tabIndex={0}
              name="agency"
              label="Agência"
              error={errors.agency?.message}
              type="text"
              inputMode="numeric"
              maxLength={4}
            />
            <BasicInput
              tabIndex={0}
              name="account"
              label="Conta"
              error={errors.account?.message}
              type="text"
              inputMode="numeric"
              maxLength={11}
            />
          </div>
        </FormProvider>
      </div>
      <div className="flex justify-end gap-5 max-sm:flex-col max-sm:gap-3">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02 max-sm:order-2"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white max-sm:order-1"
          onClick={handleSubmit(submitForm)}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
